import React from 'react';
import { Icon } from '../Icon/Icon';
import type {
  PatientFormLookupFn,
  PublicFormLookupSource,
  ResolvedFormBlock,
  ResolvedFormBlockConfig,
  ResolvedFormCatalogField,
  ResolvedFormFieldOverride,
} from '../../forms/patientFill/types';
import {
  FieldControl,
  FieldLabel,
  catalogFieldLabel,
  mapFieldControlType,
  normalizeOptions,
  type FieldControlType,
  type LookupSearchFn,
} from './PatientFormFields';

export interface PatientFormBlockProps {
  block: ResolvedFormBlock;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  onLookupSearch?: PatientFormLookupFn;
}

function readText(config: ResolvedFormBlockConfig): string {
  return (
    (typeof config.label === 'string' && config.label) ||
    (typeof config.text === 'string' && config.text) ||
    ''
  );
}

function resolveSmartBlockTitle(block: ResolvedFormBlock, cfg: ResolvedFormBlockConfig): string {
  if (typeof cfg.custom_label === 'string' && cfg.custom_label.trim()) return cfg.custom_label.trim();
  const configLabel = readText(cfg).trim();
  if (configLabel) return configLabel;
  if (typeof block.section_name === 'string' && block.section_name.trim()) return block.section_name.trim();
  if (typeof block.label === 'string' && block.label.trim()) return block.label.trim();
  if (typeof block.catalog?.category === 'string' && block.catalog.category.trim()) {
    return block.catalog.category.trim();
  }
  return 'Section';
}

function mapFieldLookupSource(
  fieldType: string | undefined,
  context?: { fieldId?: string; blockType?: string },
): PublicFormLookupSource | undefined {
  switch (fieldType) {
    case 'patient_allergies':
      return 'allergies';
    case 'patient_conditions':
      return 'conditions';
    case 'patient_medications':
      return 'medications';
    case 'patient_referral_source':
      return 'referral_sources';
    case 'search_multi': {
      const fieldId = context?.fieldId;
      const blockType = context?.blockType;
      if (fieldId === 'allergies' || blockType === 'sb_allergy_list' || blockType === 'patient_allergies') {
        return 'allergies';
      }
      if (fieldId === 'conditions' || blockType === 'sb_medical_history' || blockType === 'patient_conditions') {
        return 'conditions';
      }
      if (fieldId === 'medications' || blockType === 'sb_current_medications' || blockType === 'patient_medications') {
        return 'medications';
      }
      return undefined;
    }
    case 'search': {
      // Single search: carrier (structured) or referral (stays a plain string).
      const fieldId = context?.fieldId;
      const blockType = context?.blockType;
      if (fieldId === 'carrier' || blockType === 'sb_dental_insurance' || blockType === 'sb_medical_insurance') {
        return 'insurance_carriers';
      }
      if (fieldId === 'source' || blockType === 'sb_referral_source') {
        return 'referral_sources';
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

/** Prefer the backend-injected `lookup_source`; fall back to the FE derivation. */
function resolveLookupSource(
  field: { type?: string; lookup_source?: unknown },
  context: { fieldId?: string; blockType?: string },
): PublicFormLookupSource | undefined {
  const injected = field.lookup_source;
  if (typeof injected === 'string' && injected) return injected as PublicFormLookupSource;
  return mapFieldLookupSource(field.type, context);
}

/**
 * The control a lookup field renders through. Referral source (and anything
 * without a lookup source) is unchanged — referral stays a plain string per
 * FD142. The four structured sources render the choice control: multi
 * (`search_multi`) as tags, a single `search` (carrier) as a single lookup.
 */
function effectiveControlType(
  rawType: FieldControlType,
  source: PublicFormLookupSource | undefined,
): FieldControlType {
  if (!source || source === 'referral_sources') return rawType;
  return rawType === 'tags' ? 'tags' : 'lookup';
}

function isPatientBlock(block: ResolvedFormBlock): boolean {
  return block.block_kind === 'patient_info' || block.block_type.startsWith('patient_');
}

function isSmartBlock(block: ResolvedFormBlock): boolean {
  return block.block_kind === 'smart_block' || block.block_type.startsWith('sb_');
}

/** A single labelled control (used by basic, patient, and smart sub-fields). */
function FieldRow({
  label,
  required,
  hint,
  badge,
  locked,
  type,
  value,
  onChange,
  blockId,
  fieldId,
  options,
  placeholder,
  content,
  error,
  disabled,
  onLookupSearch,
  allowOther,
  columns,
  accept,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  badge?: { label: string; color: string };
  locked?: boolean;
  type: FieldControlType;
  value: unknown;
  onChange: (value: unknown) => void;
  blockId?: string;
  fieldId?: string;
  options?: string[];
  placeholder?: string;
  content?: string;
  error?: string;
  disabled?: boolean;
  onLookupSearch?: LookupSearchFn;
  allowOther?: boolean;
  columns?: number;
  accept?: string;
}) {
  const labelInline = type === 'checkbox' || type === 'display';
  return (
    <div>
      {labelInline ? null : (
        <FieldLabel
          label={label}
          {...(required !== undefined ? { required } : {})}
          {...(locked !== undefined ? { locked } : {})}
          {...(badge ? { badge } : {})}
          {...(hint ? { hint } : {})}
        />
      )}
      <FieldControl
        type={type}
        value={value}
        onChange={onChange}
        {...(blockId ? { blockId } : {})}
        {...(fieldId ? { fieldId } : {})}
        {...(options ? { options: type === 'checkbox' ? [label] : options } : type === 'checkbox' ? { options: [label] } : {})}
        {...(placeholder ? { placeholder } : {})}
        {...(content !== undefined ? { content } : {})}
        {...(locked !== undefined ? { locked } : {})}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(error ? { error: true } : {})}
        {...(onLookupSearch ? { onLookupSearch } : {})}
        {...(allowOther ? { allowOther } : {})}
        {...(columns ? { columns } : {})}
        {...(accept ? { accept } : {})}
      />
      {error && !labelInline ? <div className="mt-1.5 text-[11.5px] text-[var(--terracotta)]">{error}</div> : null}
    </div>
  );
}

function SmartBlock({ block, value, onChange, disabled, onLookupSearch, error }: PatientFormBlockProps) {
  const cfg = block.config;
  const overrides = (cfg.field_overrides ?? {}) as Record<string, ResolvedFormFieldOverride>;
  const fields = block.catalog?.fields ?? [];
  const record = (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<string, unknown>;
  const label = resolveSmartBlockTitle(block, cfg);
  const intro = typeof cfg.custom_intro === 'string' ? cfg.custom_intro : undefined;
  const outro = typeof cfg.custom_outro === 'string' ? cfg.custom_outro : undefined;
  const accent = 'var(--ai-3)';

  const visibleFields = fields.filter((f) => overrides[f.id]?.visible !== false);

  const setField = (fieldId: string, next: unknown) => {
    onChange({ ...record, [fieldId]: next });
  };

  return (
    // W18/D-forms: each smart block reads as a titled card (border + surface),
    // so a patient can see where they are. Loose basic/patient_info runs stay flat.
    <section className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)] sm:p-5">
      <header className="mb-3.5 flex items-center gap-2.5 border-b border-[var(--cream-2)] pb-3">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `color-mix(in srgb, ${accent} 12%, var(--cream-1))`, color: accent }}
        >
          <Icon kind="sparkle" size={15} />
        </span>
        <span className="text-[14px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">{label}</span>
      </header>

      {intro ? (
        <div className="mb-3.5 rounded-[var(--r-md)] border-l-[3px] border-[var(--gold)] bg-[var(--cream-0)] px-3 py-2.5 text-[12.5px] leading-relaxed text-[var(--ink-1)]">
          {intro}
        </div>
      ) : null}

      {error ? (
        <div className="mb-3.5 flex items-start gap-2 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--terracotta)_30%,var(--cream-2))] bg-[color-mix(in_srgb,var(--terracotta)_8%,var(--cream-1))] px-3.5 py-2.5 text-[12.5px] leading-snug text-[var(--terracotta)]">
          <Icon kind="alert" size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="flex flex-col gap-3.5">
        {visibleFields.map((field: ResolvedFormCatalogField) => {
          const ov = overrides[field.id];
          const fieldLabel = ov?.label ?? catalogFieldLabel(field);
          const required = ov?.required ?? field.required ?? false;
          const options = normalizeOptions(ov?.options ?? field.options);
          const lookupSource = resolveLookupSource(field, {
            fieldId: field.id,
            blockType: block.block_type,
          });
          const type = effectiveControlType(mapFieldControlType(field.type ?? 'text'), lookupSource);
          const content =
            typeof (field as { content?: unknown }).content === 'string'
              ? (field as { content?: string }).content
              : typeof field.prefill === 'string'
                ? field.prefill
                : undefined;
          return (
            <FieldRow
              key={field.id}
              label={fieldLabel}
              required={required}
              type={type}
              value={record[field.id]}
              onChange={(v) => {
                setField(field.id, v);
              }}
              blockId={block.id}
              {...(type === 'signature' ? { fieldId: field.id } : {})}
              {...(options.length ? { options } : {})}
              {...(field.hint ? { hint: field.hint } : {})}
              {...(content !== undefined ? { content } : {})}
              {...(type === 'display' ? { locked: field.type === 'rich_text_locked' } : {})}
              {...(disabled !== undefined ? { disabled } : {})}
              {...(lookupSource && onLookupSearch
                ? {
                    onLookupSearch: (query: string) => onLookupSearch(lookupSource, query),
                  }
                : {})}
            />
          );
        })}
      </div>

      {outro ? (
        <div className="mt-3.5 rounded-[var(--r-md)] border-l-[3px] border-[var(--gold)] bg-[var(--cream-0)] px-3 py-2.5 text-[12.5px] leading-relaxed text-[var(--ink-1)]">
          {outro}
        </div>
      ) : null}
    </section>
  );
}

export function PatientFormBlock({
  block,
  value,
  onChange,
  error,
  disabled,
  onLookupSearch,
}: PatientFormBlockProps) {
  const cfg = block.config;

  if (isSmartBlock(block)) {
    return (
      <SmartBlock
        block={block}
        value={value}
        onChange={onChange}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(onLookupSearch ? { onLookupSearch } : {})}
        {...(error ? { error } : {})}
      />
    );
  }

  switch (block.block_type) {
    case 'heading':
      return (
        <h2 className="text-[22px] font-semibold tracking-[-0.03em] text-[var(--ink-0)]">{readText(cfg) || 'Heading'}</h2>
      );
    case 'paragraph':
      return <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-[var(--ink-1)]">{readText(cfg)}</p>;
    case 'section_break':
      return <div className="my-1 h-px bg-[var(--cream-2)]" />;
    case 'rich_text_display':
      return (
        <FieldRow
          label={readText(cfg)}
          type="display"
          value={value}
          onChange={onChange}
          content={readText(cfg)}
        />
      );
    default: {
      const patient = isPatientBlock(block);
      // Top-level `label`/`section_name` are the catalog's generic name; the author's label lives in config.
      const label =
        readText(cfg) ||
        (typeof block.label === 'string' && block.label) ||
        (typeof block.section_name === 'string' && block.section_name) ||
        'Field';
      const required = block.is_required || cfg.required === true;
      const options = normalizeOptions(cfg.options);
      const hint = typeof cfg.help_text === 'string' ? cfg.help_text : undefined;
      const placeholder = typeof cfg.placeholder === 'string' ? cfg.placeholder : undefined;
      const lookupSource = resolveLookupSource(
        { type: block.block_type, lookup_source: (block as { lookup_source?: unknown }).lookup_source },
        { blockType: block.block_type },
      );
      const baseType = effectiveControlType(mapFieldControlType(block.block_type), lookupSource);
      const type: FieldControlType = cfg.input_format === 'ssn' && baseType === 'text' ? 'ssn_full' : baseType;
      const allowOther = cfg.allow_other === true;
      const columns = typeof cfg.columns === 'number' ? cfg.columns : undefined;
      const accept = typeof cfg.accept === 'string' && cfg.accept ? cfg.accept : undefined;
      const iconBadge: { label: string; color: string } | undefined = patient
        ? { label: 'PMS', color: 'var(--info)' }
        : undefined;
      return (
        <FieldRow
          label={label}
          required={required}
          type={type}
          value={value}
          onChange={onChange}
          blockId={block.id}
          {...(options.length ? { options } : {})}
          {...(hint ? { hint } : {})}
          {...(placeholder ? { placeholder } : {})}
          {...(iconBadge ? { badge: iconBadge } : {})}
          {...(error ? { error } : {})}
          {...(disabled !== undefined ? { disabled } : {})}
          {...(lookupSource && onLookupSearch
            ? {
                onLookupSearch: (query: string) => onLookupSearch(lookupSource, query),
              }
            : {})}
          {...(allowOther ? { allowOther } : {})}
          {...(columns ? { columns } : {})}
          {...(accept ? { accept } : {})}
        />
      );
    }
  }
}
